import { jwtDecode } from "jwt-decode";
import { STORAGE_KEYS } from "./storageKeys";

const safeWindow = () => globalThis.window ?? null;

function getStorage() {
  if (typeof sessionStorage === "undefined") return null;
  return sessionStorage;
}

function getLegacyStorage() {
  if (typeof localStorage === "undefined") return null;
  return localStorage;
}

function readTokenFromStorage() {
  const storage = getStorage();
  const legacy = getLegacyStorage();

  const namespacedToken = storage?.getItem(STORAGE_KEYS.token);
  if (namespacedToken) return normalizeToken(namespacedToken);

  const legacyToken =
    legacy?.getItem(STORAGE_KEYS.token) ??
    legacy?.getItem("token") ??
    legacy?.getItem("jwt") ??
    null;
  return normalizeToken(legacyToken);
}

function dispatchAuthEvent(type) {
  const w = safeWindow();
  if (!w) return;
  w.dispatchEvent(new Event(type));
}

function normalizeToken(token) {
  if (typeof token !== "string") return token;
  if (token.startsWith("Bearer ")) return token.slice(7).trim();
  return token.trim();
}

function normalizeClaimInt(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const MAX_JWT_STRING_LENGTH = 16384;
const JWT_COMPACT_FORMAT =
  /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

function sanitizeJwtForStorage(raw) {
  const normalized = normalizeToken(raw);
  if (typeof normalized !== "string") return null;
  const trimmed = normalized.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_JWT_STRING_LENGTH) {
    return null;
  }
  if (!JWT_COMPACT_FORMAT.test(trimmed)) {
    return null;
  }
  try {
    jwtDecode(trimmed);
    return trimmed;
  } catch {
    return null;
  }
}

export function decodeJwtToPrincipal(token) {
  try {
    const normalized = normalizeToken(token);
    const claims = jwtDecode(normalized);

    const rawRole = claims?.role;
    const role =
      typeof rawRole === "string" && rawRole.trim() ? rawRole.toLowerCase() : "vendor";

    const expRaw = claims?.exp;
    const expSeconds =
      typeof expRaw === "number"
        ? expRaw
        : typeof expRaw === "string"
          ? Number(expRaw)
          : null;
    const exp = expSeconds && Number.isFinite(expSeconds) ? expSeconds * 1000 : null;

    const userId =
      normalizeClaimInt(claims?.["login-id"]) ?? normalizeClaimInt(claims?.info?.loginId);
    const vendorId =
      normalizeClaimInt(claims?.["vendor-id"]) ?? normalizeClaimInt(claims?.info?.vendorId);
    const isImpersonated =
      claims?.["is-impersonator"] === true || claims?.impersonificator != null;

    return {
      userId,
      vendorId,
      role,
      exp,
      sub: claims?.sub ?? null,
      impersonificator: isImpersonated ? claims?.impersonificator ?? true : null,
      isImpersonated,
    };
  } catch {
    return {
      userId: null,
      vendorId: null,
      role: "vendor",
      exp: null,
      sub: null,
      impersonificator: null,
      isImpersonated: false,
    };
  }
}

export const AuthSessionManager = {
  getToken() {
    return readTokenFromStorage();
  },

  getRole() {
    const storage = getStorage();
    if (!storage) return null;
    return storage.getItem(STORAGE_KEYS.role);
  },

  getPrincipal() {
    const token = readTokenFromStorage();
    if (token) {
      return decodeJwtToPrincipal(token);
    }
    const storage = getStorage();
    if (!storage) return null;
    const raw = storage.getItem(STORAGE_KEYS.principal);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },

  bootstrap() {
    const storage = getStorage();
    const token = readTokenFromStorage();
    if (!storage) {
      return { token, role: null, principal: null };
    }

    let role = storage.getItem(STORAGE_KEYS.role);
    let principal = null;

    if (token) {
      principal = decodeJwtToPrincipal(token);
      role = principal?.role ?? role;
      if (role) {
        storage.setItem(STORAGE_KEYS.role, role);
      }
      storage.setItem(STORAGE_KEYS.principal, JSON.stringify(principal));
    } else {
      const principalRaw = storage.getItem(STORAGE_KEYS.principal);
      if (principalRaw) {
        try {
          principal = JSON.parse(principalRaw);
        } catch {
          principal = null;
        }
      }
    }

    return { token, role, principal };
  },

  loginWithToken(token) {
    if (!token) return;
    const sanitized = sanitizeJwtForStorage(token);
    if (!sanitized) return;
    const principal = decodeJwtToPrincipal(sanitized);
    const now = Date.now();
    if (typeof principal?.exp === "number" && principal.exp <= now) {
      throw new Error("TOKEN_EXPIRED");
    }
    const storage = getStorage();
    if (!storage) return;

    storage.setItem(STORAGE_KEYS.token, sanitized);
    storage.setItem(STORAGE_KEYS.role, principal.role);
    storage.setItem(STORAGE_KEYS.principal, JSON.stringify(principal));

    dispatchAuthEvent("wcp:auth:change");
    return principal;
  },

  logout() {
    const storage = getStorage();
    if (storage) {
      storage.removeItem(STORAGE_KEYS.token);
      storage.removeItem(STORAGE_KEYS.role);
      storage.removeItem(STORAGE_KEYS.principal);
    }
    dispatchAuthEvent("wcp:auth:logout");
  },
};
